import React, { useEffect, useRef, useState, useLayoutEffect, useCallback } from 'react'
import { motion, useTransform, useSpring, useViewportScroll } from 'framer-motion'
import styled from 'styled-components'
import media from 'css-in-js-media'
import ResizeObserver from 'resize-observer-polyfill'
import { useCurrentHeight } from './windowSize'

const HighlightStyle = styled.div``

const HighlightAnimation = styled(motion.div)`
    ${media('<desktop')} {
        transform: none !important;
        opacity: 1 !important;
        width: 100%;
    }
    ${media('>=desktop')} {
        will-change: transform, opacity;
    }
`

const AnimatedSection = ({ children }) => {
    const { scrollY } = useViewportScroll()
    const windowHeight = useCurrentHeight()
    const highlights = useRef(null)
    const [highlightPos, setHighlightPos] = useState(0)

    useEffect(() => {
        setHighlightPos(highlights.current.getBoundingClientRect().top)
    }, [highlights])

    const updateHighlights = useCallback((entries) => {
        for (let entry of entries) {
            setHighlightPos(entry.target.getBoundingClientRect().top)
        }
    }, [])

    useLayoutEffect(() => {
        const resizeObserver = new ResizeObserver((entries) => {
            updateHighlights(entries)
        })
        highlights && resizeObserver.observe(highlights.current)
        return () => resizeObserver.disconnect()
    }, [highlights])

    const highlightY = useSpring(
        useTransform(
            scrollY,
            [
                highlightPos - windowHeight,
                highlightPos - windowHeight + windowHeight / 2,
                highlightPos - windowHeight + windowHeight / 2,
                highlightPos,
            ],
            [200, 0, 0, 0]
        ),
        { damping: 13, mass: 0.27, stiffness: 75 }
    )

    const highlightOpacity = useSpring(
        useTransform(scrollY, [highlightPos - windowHeight, highlightPos - windowHeight + windowHeight / 2], [0, 1]),
        { damping: 13, mass: 0.27, stiffness: 75 }
    )

    useEffect(() => {
        const windowLoadedCallback = () => {
            setHighlightPos(highlights.current.getBoundingClientRect().top)
        }

        if (document.readyState === 'complete') {
            windowLoadedCallback()
        } else {
            window.addEventListener('load', windowLoadedCallback)
            return () => document.removeEventListener('load', windowLoadedCallback)
        }
    }, [])

    return (
        <>
            <HighlightStyle>
                <div ref={highlights}></div>
                <HighlightAnimation
                    style={{
                        y: highlightY,
                        opacity: highlightOpacity,
                    }}
                >
                    {children}
                </HighlightAnimation>
            </HighlightStyle>
        </>
    )
}

export default AnimatedSection
